import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Main, Text, Background, Shade, Border } from 'styles/themeColors';
import { Space } from 'styles/themeSpaces';
import { BorderRadius } from 'styles/themeBorderRadius';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import { useAPI } from 'lib/API';
import * as ChatbotRequests from 'requests/aiChatAssistant';
import type { ChatbotResponse, ChatbotLimitsResponse } from 'requests/aiChatAssistant';
import { renderToast, toast } from 'components/UI/ToastNotification';
import {
  CHATBOT_CHAR_LIMIT,
  CHATBOT_NEAR_LIMIT_RATIO,
  CHATBOT_TEXTAREA_MAX_HEIGHT,
  CHATBOT_TITLE,
  CHATBOT_WELCOME_TITLE,
  CHATBOT_WELCOME_TEXT,
  CHATBOT_INPUT_PLACEHOLDER,
  CHATBOT_CLOSE_ARIA_LABEL,
  CHATBOT_SEND_ARIA_LABEL,
  CHATBOT_FALLBACK_RESPONSE,
  CHATBOT_ERROR_TOAST,
  CHATBOT_ERROR_MESSAGE,
  CHATBOT_CHAR_LIMIT_WARNING,
  CHATBOT_COLOR_AT_LIMIT,
  CHATBOT_COLOR_NEAR_LIMIT,
  CHATBOT_TYPING_DOT_DELAYS,
  CHATBOT_AVATAR_ICON_SIZE,
  CHATBOT_MESSAGE_LINE_HEIGHT,
  CHATBOT_LOW_REQUEST_THRESHOLD,
  CHATBOT_COLOR_LOW_REQUEST,
  CHATBOT_LIMIT_EXCEEDED_MESSAGE,
  CHATBOT_AI_DISCLAIMER,
} from 'constants/aiChatAssistant';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';

const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ChatWindowContainer = styled.div<{ $isMaximized: boolean }>`
  position: fixed;
  bottom: ${props => props.$isMaximized ? '50%' : '88px'};
  right: ${props => props.$isMaximized ? '50%' : '24px'};
  transform: ${props => props.$isMaximized ? 'translate(50%, 50%)' : 'none'};
  width: ${props => props.$isMaximized ? '90vw' : '380px'};
  max-width: ${props => props.$isMaximized ? '1200px' : 'none'};
  height: ${props => props.$isMaximized ? '85vh' : '600px'};
  max-height: ${props => props.$isMaximized ? '90vh' : 'calc(100vh - 120px)'};
  background-color: ${Background.white};
  border-radius: 12px;
  box-shadow: 0px 16px 20px rgba(47, 66, 83, 0.15);
  display: flex;
  flex-direction: column;
  z-index: 9998;
  animation: ${slideInUp} 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

const ChatHeader = styled.div`
  background: linear-gradient(135deg, ${Main.brandLight} 0%, ${Main.brandDark} 100%);
  border-radius: 12px 12px 0 0;
  padding: ${Space.spBasePlus};
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${Text.whitePrimary};
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${Space.spBase};
  font-weight: bold;
  font-size: 16px;
`;

const HeaderButton = styled.button`
  background: transparent;
  border: none;
  color: ${Text.whitePrimary};
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const HeaderButtons = styled.div`
  display: flex;
  align-items: center;
  gap: ${Space.spHalf};
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${Space.spBasePlus};
  display: flex;
  flex-direction: column;
  gap: ${Space.spBase};

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${Background.shaded};
    border-radius: ${BorderRadius.s};
  }

  &::-webkit-scrollbar-thumb {
    background: ${Shade.shade230};
    border-radius: ${BorderRadius.s};
  }
`;

const MessageWrapper = styled.div<{ $isUser: boolean }>`
  display: flex;
  justify-content: ${props => props.$isUser ? 'flex-end' : 'flex-start'};
  align-items: flex-start;
  gap: ${Space.spHalf};
`;

const MessageAvatar = styled.div<{ $isUser: boolean }>`
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.$isUser ? Main.brandLight : Shade.shade230};
  color: ${props => props.$isUser ? Text.whitePrimary : Text.primary};
`;

const MessageBubble = styled.div<{ $isUser: boolean }>`
  max-width: 75%;
  padding: ${Space.spHalf} ${Space.spBase};
  border-radius: ${props => props.$isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px'};
  background-color: ${props => props.$isUser ? Main.brandLight : Background.shaded};
  color: ${props => props.$isUser ? Text.whitePrimary : Text.primary};
  font-size: 14px;
  line-height: 1.5;
  word-wrap: break-word;

  /* Markdown HTML styling - targets standard elements emitted by marked */

  strong {
    font-weight: 700;
  }

  em {
    font-style: italic;
  }

  p {
    margin: 0 0 8px 0;

    &:last-child {
      margin-bottom: 0;
    }
  }

  code {
    background-color: ${Background.shaded};
    padding: 2px 6px;
    border-radius: 3px;
    font-family: 'Courier New', monospace;
    font-size: 0.9em;
  }

  pre {
    background-color: ${Background.shaded};
    padding: 12px;
    border-radius: 6px;
    overflow-x: auto;
    margin: 8px 0;

    code {
      background-color: transparent;
      padding: 0;
      border-radius: 0;
      font-family: 'Courier New', monospace;
      font-size: 0.9em;
    }
  }

  a {
    color: ${props => props.$isUser ? Text.whitePrimary : Main.accent};
    text-decoration: underline;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.8;
      text-decoration: none;
    }

    &:active {
      opacity: 0.6;
    }
  }

  ol,
  ul {
    margin: 8px 0;
    padding-left: 20px;
    text-align: left;
  }

  ul {
    list-style-type: disc;
  }

  ol {
    list-style-type: decimal;
  }

  ul ul,
  ol ul {
    list-style-type: circle;
    margin-left: 16px;
    font-size: 0.95em;
    color: ${props => props.$isUser ? Text.whitePrimary : Text.secondary};
  }

  li {
    margin: 4px 0;
  }

  ul > li,
  ol > li {
    font-weight: 500;
  }
`;

const InputContainer = styled.div`
  padding: ${Space.spBase};
  border-top: 1px solid ${Border.light};
  display: flex;
  flex-direction: column;
  gap: ${Space.spHalf};
  background-color: ${Background.white};
  border-radius: 0 0 12px 12px;
`;

const InputRow = styled.div`
  display: flex;
  gap: ${Space.spHalf};
`;

const InputField = styled.textarea`
  flex: 1;
  border: 1px solid ${Border.main};
  border-radius: ${BorderRadius.m};
  padding: ${Space.spHalf} ${Space.spBase};
  font-size: 14px;
  font-family: inherit;
  resize: none;
  min-height: 40px;
  max-height: 120px;
  outline: none;
  transition: border-color 0.2s;

  &::placeholder {
    color: ${Text.placeholder};
  }

  &:focus {
    border-color: ${Main.accent};
  }
`;

const SendButton = styled.button<{ $disabled: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background-color: ${props => props.$disabled ? Shade.shade230 : Main.accent};
  color: ${Text.whitePrimary};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  opacity: ${props => props.$disabled ? 0.6 : 1};

  &:hover:not(:disabled) {
    background-color: ${Main.accent};
    filter: brightness(0.9);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }
`;

const CharCounter = styled.div<{ $isNearLimit: boolean; $atLimit: boolean }>`
  font-size: 12px;
  text-align: right;
  color: ${props => {
    if (props.$atLimit) return CHATBOT_COLOR_AT_LIMIT;
    if (props.$isNearLimit) return CHATBOT_COLOR_NEAR_LIMIT;
    return Text.secondary;
  }};
  font-weight: ${props => props.$atLimit ? '600' : '400'};
  transition: color 0.2s;
`;

const RequestLimitIndicator = styled.div`
  font-size: 11px;
  text-align: right;
  color: ${CHATBOT_COLOR_LOW_REQUEST};
  font-weight: bold;
`;

const AiDisclaimer = styled.div`
  font-size: 11px;
  text-align: center;
  color: ${Text.secondary};
`;

const typingIndicator = keyframes`
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-4px);
  }
`;

const TypingIndicator = styled.div`
  display: flex;
  gap: 4px;
  padding: 12px 16px;
`;

const TypingDot = styled.div<{ $delay: number }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${Shade.shade450};
  animation: ${typingIndicator} 1.4s infinite ease-in-out;
  animation-delay: ${props => props.$delay}s;
`;

const WelcomeMessage = styled.div`
  text-align: center;
  padding: ${Space.sp3x};
  color: ${Text.secondary};
`;

const WelcomeTitle = styled.h4`
  margin: 0 0 ${Space.spHalf} 0;
  color: ${Text.primary};
`;

const WelcomeText = styled.p`
  margin: 0;
  font-size: 14px;
`;

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
  isHtml?: boolean; // Flag to indicate if content should be rendered as HTML
}

interface Props {
  onClose: () => void;
  messages: Message[];
  setMessages: (messages: Message[] | ((prev: Message[]) => Message[])) => void;
  setUnreadCount: (count: number | ((prev: number) => number)) => void;
}

const ChatbotWindow = ({ onClose, messages, setMessages, setUnreadCount }: Props) => {
  const [inputValue, setInputValue] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const [remainingRequests, setRemainingRequests] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const charCount = inputValue.length;
  const isNearLimit = charCount >= CHATBOT_CHAR_LIMIT * CHATBOT_NEAR_LIMIT_RATIO;
  const atLimit = charCount >= CHATBOT_CHAR_LIMIT;
  const limitReached = remainingRequests === 0;

  const sendMessage = useAPI({
    deferFn: ChatbotRequests.sendMessage,
    onResolve: (data: ChatbotResponse) => {
      const assistantMessage: Message = {
        role: 'assistant',
        content: data.answer || CHATBOT_FALLBACK_RESPONSE,
        timestamp: data.timestamp,
        isHtml: true // Mark as HTML to enable rich formatting
      };
      setMessages((prev: Message[]) => [...prev, assistantMessage]);

      // Store sessionId if returned by the AI service (for maintaining conversation context)
      if (data.sessionId) {
        setSessionId(data.sessionId);
      }

      if (data.remainingRequests !== undefined) {
        setRemainingRequests(data.remainingRequests);
      }
    },
    onReject: (err: Error) => {
      renderToast(toast.TYPE.ERROR, err.message || CHATBOT_ERROR_TOAST);
      setMessages((prev: Message[]) => [...prev, {
        role: 'assistant',
        content: CHATBOT_ERROR_MESSAGE,
        isHtml: false
      }]);
    }
  });

  // Fetch request limits on mount
  useAPI<ChatbotLimitsResponse>({
    promiseFn: ChatbotRequests.getRequestLimits,
    onResolve: (data: ChatbotLimitsResponse) => {
      setRemainingRequests(data.remainingRequests);
    }
  });

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sendMessage.isPending]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, CHATBOT_TEXTAREA_MAX_HEIGHT) + 'px';
    }
  }, [inputValue]);

  const handleSend = () => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput || sendMessage.isPending || limitReached) return;

    const userMessage: Message = {
      role: 'user',
      content: trimmedInput
    };

    setMessages((prev: Message[]) => [...prev, userMessage]);
    setInputValue('');

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    // Run the sendMessage with the question and sessionId (if available)
    // Pass sessionId as additional context to maintain conversation
    if (sessionId) {
      sendMessage.run(trimmedInput, sessionId);
    } else {
      sendMessage.run(trimmedInput);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (newValue.length <= CHATBOT_CHAR_LIMIT) {
      setInputValue(newValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <ChatWindowContainer $isMaximized={isMaximized}>
      <ChatHeader>
        <HeaderTitle>
          <SmartToyIcon />
          <span>{CHATBOT_TITLE}</span>
        </HeaderTitle>
        <HeaderButtons>
          <HeaderButton
            onClick={() => setIsMaximized(!isMaximized)}
            aria-label={isMaximized ? 'Minimize' : 'Maximize'}
            title={isMaximized ? 'Minimize' : 'Maximize'}
          >
            {isMaximized ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
          </HeaderButton>
          <HeaderButton onClick={onClose} aria-label={CHATBOT_CLOSE_ARIA_LABEL}>
            <CloseIcon />
          </HeaderButton>
        </HeaderButtons>
      </ChatHeader>

      <MessagesContainer>
        {messages.length === 0 ? (
          <WelcomeMessage>
            <WelcomeTitle>{CHATBOT_WELCOME_TITLE}</WelcomeTitle>
            <WelcomeText>{CHATBOT_WELCOME_TEXT}</WelcomeText>
          </WelcomeMessage>
        ) : (
          messages.map((message, index) => (
            <MessageWrapper key={index} $isUser={message.role === 'user'}>
              {message.role === 'assistant' && (
                <MessageAvatar $isUser={false}>
                  <SmartToyIcon style={{ fontSize: CHATBOT_AVATAR_ICON_SIZE }} />
                </MessageAvatar>
              )}
              <MessageBubble $isUser={message.role === 'user'}>
                {message.role === 'assistant' && message.isHtml ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: message.content }}
                    style={{
                      lineHeight: CHATBOT_MESSAGE_LINE_HEIGHT,
                      wordWrap: 'break-word'
                    }}
                  />
                ) : (
                  message.content
                )}
              </MessageBubble>
              {message.role === 'user' && (
                <MessageAvatar $isUser={true}>
                  <PersonIcon style={{ fontSize: CHATBOT_AVATAR_ICON_SIZE }} />
                </MessageAvatar>
              )}
            </MessageWrapper>
          ))
        )}

        {sendMessage.isPending && (
          <MessageWrapper $isUser={false}>
            <MessageAvatar $isUser={false}>
              <SmartToyIcon style={{ fontSize: CHATBOT_AVATAR_ICON_SIZE }} />
            </MessageAvatar>
            <MessageBubble $isUser={false}>
              <TypingIndicator>
                {CHATBOT_TYPING_DOT_DELAYS.map(delay => (
                  <TypingDot key={delay} $delay={delay} />
                ))}
              </TypingIndicator>
            </MessageBubble>
          </MessageWrapper>
        )}

        <div ref={messagesEndRef} />
      </MessagesContainer>

      <InputContainer>
        <InputRow>
          <InputField
            ref={textareaRef}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={CHATBOT_INPUT_PLACEHOLDER}
            disabled={sendMessage.isPending || limitReached}
            maxLength={CHATBOT_CHAR_LIMIT}
          />
          <SendButton
            onClick={handleSend}
            $disabled={!inputValue.trim() || sendMessage.isPending || limitReached}
            aria-label={CHATBOT_SEND_ARIA_LABEL}
          >
            <SendIcon style={{ fontSize: CHATBOT_AVATAR_ICON_SIZE }} />
          </SendButton>
        </InputRow>
        <FlexboxContainerComponent gap='10px' justifyContent='flex-end' alignItems='center'>
            {remainingRequests !== null && remainingRequests <= CHATBOT_LOW_REQUEST_THRESHOLD && (
          <RequestLimitIndicator>
            {remainingRequests === 0
              ? CHATBOT_LIMIT_EXCEEDED_MESSAGE
              : `${remainingRequests} messages left today`}
          </RequestLimitIndicator>
        )}
        <CharCounter $isNearLimit={isNearLimit} $atLimit={atLimit}>
          {atLimit
            ? CHATBOT_CHAR_LIMIT_WARNING
            : isNearLimit
            ? `${charCount}/${CHATBOT_CHAR_LIMIT} characters`
            : `${charCount}/${CHATBOT_CHAR_LIMIT}`}
        </CharCounter>
        </FlexboxContainerComponent>
        <AiDisclaimer>{CHATBOT_AI_DISCLAIMER}</AiDisclaimer>
      </InputContainer>
    </ChatWindowContainer>
  );
};

export default ChatbotWindow;
